import { createApiClient } from '@deloitte-us-consulting/gp-sdk';
import type { AppCategoryId, AppLifecycleId } from '../pages/home/appStoreVocabulary';

/**
 * PluginRegistry client. The shell learns which apps exist and how to open each
 * one: `remoteUrl` (path to remoteEntry.js) for a HOSTED app mounted as a Module
 * Federation remote, or `externalUrl` for a LINKED app — one with its own auth and
 * hosting elsewhere (e.g. a sibling Deloitte app) that the shell only opens in a
 * new tab. An entry with neither is headless (chat-only) and is filtered out of
 * nav/routes by `uiApps()`.
 *
 * Resolution order:
 *   1. `GET /api/registry` — the real platform API (once deployed / DynamoDB-backed).
 *   2. `/registry.dev.json` — a same-origin local file (no AWS) used by the smoke test.
 *   3. `[]` — offline dev with neither. `loadRegistry` reports this case as
 *      `failed` when the API threw, so the UI can say so instead of "no apps".
 */
export interface PluginEntry {
  appId: string;
  displayName: string;
  remoteUrl?: string;
  /** An absolute https:// URL for a LINKED app hosted outside this platform. */
  externalUrl?: string;
  navIcon?: string;
  navOrder?: number;
  /** Role ids required to see this app — the user needs at least one of
   *  them (ADR 0012 decision 1/finding 1). Absent/empty = no role required. */
  requiredRole?: string[];
  /** Alliance ids required to see this app (ADR 0010) — the user's alliance
   *  access must be "All" or intersect this list. Absent/empty = no alliance required. */
  requiredAlliance?: string[];
  enabled?: boolean;
  /** Whether this app is permitted to call the AI gateway. */
  aiEnabled?: boolean;
  /** App Store category (spec 2026-09-30-app-store-categories): the launcher filter
   *  chip and the tile color. An unknown id at runtime is treated as no category. */
  category?: AppCategoryId | undefined;
  /** One line shown on the App Store tile instead of the description. */
  tagline?: string | undefined;
  /** The team that owns the app, shown on the tile. */
  ownerTeam?: string | undefined;
  /** The stage the App Store tile states (ADR 0043). Read it through `appLifecycle`.
   *  An unknown id at runtime is treated as unset. */
  lifecycle?: AppLifecycleId | undefined;
  /** Retired by `lifecycle` (ADR 0043). The API converts it, but the web and the API
   *  deploy separately, so `appLifecycle` still reads it from an older API. */
  earlyWip?: boolean | undefined;
  description?: string;
}

/** An entry's stage, reading the retired earlyWip flag as in-development. */
export function appLifecycle(entry: Pick<PluginEntry, 'lifecycle' | 'earlyWip'>): AppLifecycleId | undefined {
  return entry.lifecycle ?? (entry.earlyWip ? 'in-development' : undefined);
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';

/**
 * One registry read. `failed` is true only when the platform API threw AND there
 * was no local `registry.dev.json` to fall back to, so the entry list says nothing
 * about what this user can open. An API that answers with no apps is a real
 * answer, not a failure.
 */
export interface RegistryLoad {
  entries: PluginEntry[];
  failed: boolean;
}

async function fetchRegistry(getToken: () => string | null): Promise<RegistryLoad> {
  // 1) platform API
  let apiAnswered = false;
  try {
    const api = createApiClient(API_URL, getToken);
    const entries = await api.get<PluginEntry[]>('/api/registry');
    apiAnswered = true;
    if (Array.isArray(entries) && entries.length) {
      return { entries: entries.filter((e) => e.enabled !== false), failed: false };
    }
  } catch {
    /* fall through */
  }
  // 2) local dev registry (no API / no AWS)
  try {
    const res = await fetch('/registry.dev.json', { headers: { Accept: 'application/json' } });
    if (res.ok) {
      const entries = (await res.json()) as PluginEntry[];
      return { entries: entries.filter((e) => e.enabled !== false), failed: false };
    }
  } catch {
    /* fall through */
  }
  return { entries: [], failed: !apiAnswered };
}

// Memoize so multiple routes/components share one load. Only a successful load
// stays cached: a failed one is dropped so the next caller retries, instead of
// the whole session believing the user has no apps.
let cache: Promise<RegistryLoad> | null = null;
let lastGood: PluginEntry[] | undefined;

export function loadRegistry(getToken: () => string | null): Promise<RegistryLoad> {
  if (!cache) {
    const load = fetchRegistry(getToken).then((result) => {
      if (result.failed) {
        if (cache === load) cache = null;
      } else {
        lastGood = result.entries;
      }
      return result;
    });
    cache = load;
  }
  return cache;
}

// Everyone mounted on the registry (the side nav and the launcher both are) hears
// about a retry, so one Retry recovers every surface, not just the one clicked.
type RetryListener = (load: Promise<RegistryLoad>) => void;
const retryListeners = new Set<RetryListener>();

/**
 * Re-reads the registry after a failed load and hands the new load to every
 * `onRegistryRetry` listener. Shares `loadRegistry`'s memo, so repeated calls
 * while a load is in flight (a double click) cost no extra fetch, and a call
 * after a successful load just re-delivers the cached result.
 */
export function retryRegistry(getToken: () => string | null): void {
  const load = loadRegistry(getToken);
  for (const listener of retryListeners) listener(load);
}

/** Subscribes to `retryRegistry`. Returns the unsubscribe function. */
export function onRegistryRetry(listener: RetryListener): () => void {
  retryListeners.add(listener);
  return () => {
    retryListeners.delete(listener);
  };
}

/** The entry list alone. Never rejects: a failure resolves to `[]`, which callers
 *  such as RemoteRoute (no rejection handler) rely on. */
export function getRegistry(getToken: () => string | null): Promise<PluginEntry[]> {
  return loadRegistry(getToken).then((r) => r.entries);
}

/** The entries of the last successful load, synchronously; undefined before any
 *  load has succeeded. Lets a remounting view skip its loading state. */
export function peekRegistry(): PluginEntry[] | undefined {
  return lastGood;
}

/**
 * Apps that contribute something to open — hosted (`remoteUrl`, mounts as a
 * remote) or linked (`externalUrl`, opens in a new tab). Headless (chat-only)
 * apps have neither and are excluded.
 */
export function uiApps(entries: PluginEntry[]): PluginEntry[] {
  return entries
    .filter((e) => !!e.remoteUrl || !!e.externalUrl)
    .sort((a, b) => (a.navOrder ?? 0) - (b.navOrder ?? 0));
}

/**
 * A LINKED app: lives outside the platform, opens in a new tab, never mounts as a
 * remote. `remoteUrl` wins if an entry somehow has both (the API rejects that
 * combination at write time, but this keeps read-side behavior well-defined too).
 */
export function isLinkedApp(entry: PluginEntry): boolean {
  return !entry.remoteUrl && !!entry.externalUrl;
}

/**
 * Where a link to an app goes: a linked app's externalUrl, opened in a new tab, or
 * a hosted app's shell path, routed in place. The launcher's tiles and its Quick
 * Launch both use this, so they always agree.
 */
export function appLink(entry: PluginEntry): { href: string; external: boolean } {
  const external = isLinkedApp(entry);
  return { href: external && entry.externalUrl ? entry.externalUrl : `/${entry.appId}`, external };
}
